import { jsx as _jsx } from "react/jsx-runtime";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { UserAPI } from '../apis/UserAPI';
import { saveToCache, getFromCache } from '../utils/indexedDBCache';
const APPLICATIONS_CACHE_KEY = 'applications_list';
// Module-level cache for getAllApplications (?all=true) — shared across all tabs
let cachedAllApplications = null;
let ongoingAllRequest = null;
// Create the context
const ApplicationsContext = createContext(null);
let ongoingRequest = null;
// Provider component
export const ApplicationsProvider = ({ children }) => {
    const [applicationsList, setApplicationsList] = useState([]);
    // Set the full list once
    const setApplicationList = (items) => {
        setApplicationsList(items);
    };
    const value = useMemo(() => ({ applicationsList, setApplicationList }), [applicationsList]);
    return (_jsx(ApplicationsContext.Provider, { value: value, children: children }));
};
// Hook to access the full context
export const useApplicationsContext = () => {
    const context = useContext(ApplicationsContext);
    if (!context) {
        throw new Error('useApplicationsContext must be used inside <ApplicationsProvider>');
    }
    return context;
};
// Hook to get one application by ID
export const useCurrentApplication = (id) => {
    const { applicationsList } = useApplicationsContext();
    return id ? (applicationsList.find((app) => app?.id === id) ?? null) : null;
};
// Hook to get all applications
export const useApplicationsList = () => {
    const { applicationsList, setApplicationList } = useApplicationsContext();
    // Use ref to track latest applicationsList value to avoid stale closures
    const applicationsListRef = useRef(applicationsList);
    useEffect(() => {
        applicationsListRef.current = applicationsList;
    }, [applicationsList]);
    const getApplications = useCallback(async () => {
        // 1. Check in-memory cache first (fastest)
        if (applicationsListRef.current.length > 0) {
            return applicationsListRef.current;
        }
        // 2. Check IndexedDB cache (persisted across sessions)
        try {
            const cachedApplications = await getFromCache(APPLICATIONS_CACHE_KEY);
            if (cachedApplications && cachedApplications.length > 0) {
                setApplicationList(cachedApplications);
                return cachedApplications;
            }
        }
        catch (error) {
            console.warn('Failed to retrieve applications from IndexedDB:', error);
        }
        // 3. Return ongoing request if exists
        if (ongoingRequest) {
            return ongoingRequest;
        }
        // 4. Fetch from API
        const requestPromise = (async () => {
            try {
                const response = await UserAPI.getApplications();
                const applications = response?.resource || [];
                setApplicationList(applications);
                // Store in IndexedDB for future use
                try {
                    await saveToCache(APPLICATIONS_CACHE_KEY, applications);
                }
                catch (error) {
                    console.warn('Failed to cache applications to IndexedDB:', error);
                }
                return applications;
            }
            catch (error) {
                ongoingRequest = null;
                throw error;
            }
            finally {
                ongoingRequest = null;
            }
        })();
        // Store promise to prevent duplicates
        ongoingRequest = requestPromise;
        return requestPromise;
    }, [setApplicationList]);
    // Note: Using ref for applicationsList to avoid stale closures while keeping function reference stable
    return getApplications;
};
/**
 * Hook to fetch all applications (?all=true) with module-level deduplication.
 * Multiple components mounting simultaneously will share a single in-flight request
 * and the result is cached for the lifetime of the session.
 */
export const useAllApplicationsList = () => {
    const getAllApplications = useCallback(async () => {
        // 1. Return module cache if already fetched
        if (cachedAllApplications !== null) {
            return cachedAllApplications;
        }
        // 2. Return ongoing request to deduplicate concurrent calls
        if (ongoingAllRequest) {
            return ongoingAllRequest;
        }
        // 3. Fetch from API
        const requestPromise = (async () => {
            try {
                const response = await UserAPI.getAllApplications();
                const apps = response?.resource || [];
                cachedAllApplications = apps;
                return apps;
            }
            finally {
                ongoingAllRequest = null;
            }
        })();
        ongoingAllRequest = requestPromise;
        return requestPromise;
    }, []);
    return getAllApplications;
};
/**
 * Clear the all-applications cache (e.g. on logout or after CRUD operations)
 */
export const clearAllApplicationsCache = () => {
    cachedAllApplications = null;
    ongoingAllRequest = null;
};
// ─── Roles cache ────────────────────────────────────────────────────────────
let cachedRoles = null;
let ongoingRolesRequest = null;
/**
 * Hook to fetch all roles with module-level deduplication.
 * Multiple components mounting simultaneously share a single in-flight request.
 */
export const useRolesList = () => {
    const getRoles = useCallback(async () => {
        if (cachedRoles !== null)
            return cachedRoles;
        if (ongoingRolesRequest)
            return ongoingRolesRequest;
        const requestPromise = (async () => {
            try {
                const response = await UserAPI.getRoles();
                const roles = response?.resource || [];
                cachedRoles = roles;
                return roles;
            }
            finally {
                ongoingRolesRequest = null;
            }
        })();
        ongoingRolesRequest = requestPromise;
        return requestPromise;
    }, []);
    return getRoles;
};
export const clearRolesCache = () => {
    cachedRoles = null;
    ongoingRolesRequest = null;
};
// ─── Policies cache ──────────────────────────────────────────────────────────
let cachedPoliciesData = null;
let ongoingPoliciesRequest = null;
/**
 * Hook to fetch all policies with module-level deduplication.
 * Multiple components mounting simultaneously share a single in-flight request.
 */
export const usePoliciesList = () => {
    const getPolicies = useCallback(async () => {
        if (cachedPoliciesData !== null)
            return cachedPoliciesData;
        if (ongoingPoliciesRequest)
            return ongoingPoliciesRequest;
        const requestPromise = (async () => {
            try {
                const response = await UserAPI.getPolicies();
                const policies = response?.resource || [];
                cachedPoliciesData = policies;
                return policies;
            }
            finally {
                ongoingPoliciesRequest = null;
            }
        })();
        ongoingPoliciesRequest = requestPromise;
        return requestPromise;
    }, []);
    return getPolicies;
};
export const clearPoliciesDataCache = () => {
    cachedPoliciesData = null;
    ongoingPoliciesRequest = null;
};
